import { Box, Button, Skeleton, Stack, Typography } from "@mui/joy";
import {
    Layers,
    PiggyBank,
    RefreshCw,
    Sparkles,
    Waves,
} from "lucide-react";
import type { ReactNode } from "react";

import useFinanceBreakdown, {
    formatFinanceCurrency,
    type ContractPricingResult,
} from "../../hooks/useFinanceBreakdown";
import { useEstimateContractContextManager } from "../../hooks";
import {
    materialGlassPanelSx,
    materialRowDividerSx,
} from "./materialRowLayout";

type BreakdownRow = {
    label: string;
    hint?: string;
    amount: string;
    muted?: boolean;
    emphasize?: boolean;
};

type SectionPanelProps = {
    icon: ReactNode;
    title: string;
    subtitle?: string;
    children: ReactNode;
};

function SectionPanel({ icon, title, subtitle, children }: SectionPanelProps) {
    return (
        <Box sx={{ ...materialGlassPanelSx, height: "100%" }}>
            <Box
                sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: 1.25,
                    ...materialRowDividerSx,
                }}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                        sx={{
                            display: "flex",
                            color: "text.secondary",
                            "& svg": { display: "block" },
                        }}
                    >
                        {icon}
                    </Box>
                    <Box>
                        <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                            {title}
                        </Typography>
                        {subtitle && (
                            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                </Stack>
            </Box>
            {children}
        </Box>
    );
}

function BreakdownLine({ label, hint, amount, muted, emphasize }: BreakdownRow) {
    return (
        <Box
            sx={{
                px: { xs: 2, sm: 2.5 },
                py: 1.25,
                ...materialRowDividerSx,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                bgcolor: emphasize ? "primary.softBg" : undefined,
                "&:last-of-type": { borderBottom: "none" },
            }}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    level="body-sm"
                    sx={{
                        fontWeight: muted || emphasize ? 700 : 500,
                        color: emphasize
                            ? "primary.plainColor"
                            : muted
                              ? "text.primary"
                              : "text.secondary",
                    }}
                >
                    {label}
                </Typography>
                {hint && (
                    <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                        {hint}
                    </Typography>
                )}
            </Box>
            {amount ? (
                <Typography
                    level="body-sm"
                    sx={{
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                        color: emphasize
                            ? "primary.plainColor"
                            : muted
                              ? "text.primary"
                              : "text.secondary",
                        flexShrink: 0,
                    }}
                >
                    {amount}
                </Typography>
            ) : null}
        </Box>
    );
}

/** Share-bar colors — distinct hues so roof / gutters / fees read clearly. */
const SHARE_COLORS = {
    roof: "primary.500",
    gutter: "#0D9488", // teal
    fees: "#D97706", // amber
} as const;

function CompositionRow({
    label,
    value,
    share,
    color,
    showPlus,
}: {
    label: string;
    value: string;
    share: number;
    color: string;
    showPlus?: boolean;
}) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                py: 1,
                ...materialRowDividerSx,
                "&:last-of-type": { borderBottom: "none" },
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                {showPlus ? (
                    <Typography
                        level="body-xs"
                        sx={{
                            color: "text.tertiary",
                            fontWeight: 700,
                            width: 12,
                            textAlign: "center",
                            flexShrink: 0,
                        }}
                    >
                        +
                    </Typography>
                ) : (
                    <Box sx={{ width: 12, flexShrink: 0 }} />
                )}
                <Box
                    sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "2px",
                        bgcolor: color,
                        flexShrink: 0,
                    }}
                />
                <Typography level="body-sm" sx={{ fontWeight: 600, color: "text.secondary" }}>
                    {label}
                </Typography>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ flexShrink: 0 }}>
                <Typography
                    level="body-xs"
                    sx={{
                        color: "text.tertiary",
                        fontVariantNumeric: "tabular-nums",
                        minWidth: 40,
                        textAlign: "right",
                    }}
                >
                    {share > 0 ? `${Math.round(share * 100)}%` : "—"}
                </Typography>
                <Typography
                    level="body-sm"
                    sx={{
                        fontWeight: 700,
                        fontVariantNumeric: "tabular-nums",
                        minWidth: 88,
                        textAlign: "right",
                    }}
                >
                    {value}
                </Typography>
            </Stack>
        </Box>
    );
}

function percentLabel(rate: number): string {
    const pct = rate * 100;
    return `${pct.toFixed(pct % 1 === 0 ? 0 : 2)}%`;
}

function buildSections(pricing: ContractPricingResult) {
    const d = pricing.Details;
    const money = formatFinanceCurrency;
    const feesTotal = d.generalInsuranceAmount + d.jobOverheadAmount;

    const roofDirect = d.roofMaterials + d.roofSalesTax + d.roofLabor;
    const gutterDirect = d.gutterMaterials + d.gutterLabor;
    const hasMargin =
        d.bidAmountOverride == null && d.targetMarginPercent > 0;
    const roofMargin = hasMargin ? roofDirect * d.targetMarginPercent : 0;
    const gutterMargin = hasMargin ? gutterDirect * d.targetMarginPercent : 0;
    const marginPct = percentLabel(d.targetMarginPercent);
    // Contract API bakes fees into roof/gutter; Summary still shows them separately.
    const feesInRoof = roofDirect > 0 || gutterDirect <= 0;
    const scopeRoofPrice = feesInRoof
        ? Math.max(0, pricing.RoofPrice - feesTotal)
        : pricing.RoofPrice;
    const scopeGutterPrice = !feesInRoof
        ? Math.max(0, pricing.GutterPrice - feesTotal)
        : pricing.GutterPrice;
    const hasGutterScope = scopeGutterPrice > 0 || gutterDirect > 0;
    const hasFees =
        feesTotal > 0 ||
        (d.bidAmountOverride != null && d.bidAmountOverride > 0);
    const upgradeRows = pricing.UpgradeAmount
        ? ([
              {
                  label: "Shingle upgrade",
                  hint: pricing.UpgradeNotes || undefined,
                  amount: pricing.UpgradeAmount,
              },
          ] satisfies BreakdownRow[])
        : [];

    const total = pricing.TotalPrice || 0;
    const roofShare = total > 0 ? scopeRoofPrice / total : 0;
    const gutterShare = total > 0 ? scopeGutterPrice / total : 0;
    const feesShare = total > 0 ? feesTotal / total : 0;

    return {
        hasGutterScope,
        hasFees,
        hasUpgrades: upgradeRows.length > 0,
        composition: {
            roof: money(scopeRoofPrice),
            gutter: money(scopeGutterPrice),
            fees: money(feesTotal),
            contract: pricing.TotalPriceFormatted,
            roofShare,
            gutterShare,
            feesShare,
            paymentSchedule: pricing.PaymentSchedule || "Not set in estimating",
            showGutter: hasGutterScope,
            showFees: feesTotal > 0,
        },
        roofRows: [
            {
                label: "Roof materials",
                amount: money(d.roofMaterials),
            },
            {
                label: `Sales tax (${percentLabel(d.salesTaxRate)})`,
                amount: money(d.roofSalesTax),
            },
            {
                label: "Roof labor",
                amount: money(d.roofLabor),
            },
            {
                label: "Direct roof cost",
                hint: "Materials + tax + labor",
                amount: money(roofDirect),
                muted: true,
            },
            ...(hasMargin && roofMargin > 0
                ? [
                      {
                          label: `Profit margin (${marginPct})`,
                          amount: money(roofMargin),
                      } satisfies BreakdownRow,
                  ]
                : []),
            {
                label: "Roof price",
                amount: money(scopeRoofPrice),
                emphasize: true,
            },
        ] satisfies BreakdownRow[],
        gutterRows: [
            {
                label: "Gutter & downspout materials",
                amount: money(d.gutterMaterials),
            },
            {
                label: "Gutter & downspout labor",
                amount: money(d.gutterLabor),
            },
            {
                label: "Direct gutter cost",
                hint: "Materials + labor",
                amount: money(gutterDirect),
                muted: true,
            },
            ...(hasMargin && gutterMargin > 0
                ? [
                      {
                          label: `Profit margin (${marginPct})`,
                          amount: money(gutterMargin),
                      } satisfies BreakdownRow,
                  ]
                : []),
            {
                label: "Gutter price",
                amount: money(scopeGutterPrice),
                emphasize: true,
            },
        ] satisfies BreakdownRow[],
        markupRows: [
            ...(d.bidAmountOverride != null && d.bidAmountOverride > 0
                ? [
                      {
                          label: "Manual contract amount (override)",
                          hint: "EST bid amount override is active",
                          amount: money(d.bidAmountOverride),
                          muted: true,
                      } satisfies BreakdownRow,
                  ]
                : [
                      {
                          label: `General insurance (${percentLabel(d.overheadPercent)})`,
                          amount: money(d.generalInsuranceAmount),
                      },
                      {
                          label: `Overhead (${percentLabel(d.jobOverheadPercent)})`,
                          amount: money(d.jobOverheadAmount),
                      },
                      {
                          label: "Fees total",
                          amount: money(feesTotal),
                          emphasize: true,
                      },
                  ]),
        ] satisfies BreakdownRow[],
        upgradeRows,
    };
}

export default function FinanceBreakdown({ projectId }: { projectId?: string }) {
    const { values } = useEstimateContractContextManager();
    const { pricing, loading, error, reload } = useFinanceBreakdown(
        projectId,
        values.estimateOptionId,
    );

    if (loading) {
        return (
            <Box sx={{ mt: 1 }}>
                <Stack spacing={2}>
                    <Skeleton variant="rectangular" height={120} />
                    <Skeleton variant="rectangular" height={72} />
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                            gap: 2,
                        }}
                    >
                        <Skeleton variant="rectangular" height={160} />
                        <Skeleton variant="rectangular" height={160} />
                    </Box>
                </Stack>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ mt: 2, py: 3, textAlign: "center" }}>
                <Typography level="body-sm" color="danger" sx={{ mb: 1.5 }}>
                    {error}
                </Typography>
                <Button
                    size="sm"
                    variant="soft"
                    startDecorator={<RefreshCw size={14} />}
                    onClick={() => reload()}
                    sx={{ borderRadius: "4px" }}
                >
                    Retry
                </Button>
            </Box>
        );
    }

    if (!pricing) {
        return (
            <Box sx={{ mt: 2, py: 3, textAlign: "center" }}>
                <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                    No pricing data yet. Select suppliers on materials and labor, then refresh.
                </Typography>
            </Box>
        );
    }

    const sections = buildSections(pricing);
    const { composition } = sections;

    return (
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Hero: contract total */}
            <Box sx={materialGlassPanelSx}>
                <Box
                    sx={{
                        px: { xs: 2, sm: 2.5 },
                        py: 2,
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: 2,
                        ...materialRowDividerSx,
                    }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            level="body-xs"
                            sx={{
                                color: "text.tertiary",
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                mb: 0.75,
                            }}
                        >
                            Contract total
                        </Typography>
                        <Typography
                            level="h2"
                            sx={{
                                fontWeight: 800,
                                fontVariantNumeric: "tabular-nums",
                                lineHeight: 1.1,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            {composition.contract}
                        </Typography>
                    </Box>
                    <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        startDecorator={<RefreshCw size={14} />}
                        onClick={() => reload()}
                        sx={{ borderRadius: "4px", flexShrink: 0 }}
                    >
                        Refresh
                    </Button>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 1.5, pb: 0.5 }}>
                    <Typography
                        level="body-xs"
                        sx={{
                            color: "text.tertiary",
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            mb: 1,
                        }}
                    >
                        How this total is built
                    </Typography>
                    <Box
                        sx={{
                            display: "flex",
                            height: 6,
                            borderRadius: "2px",
                            overflow: "hidden",
                            bgcolor: "background.level2",
                            mb: 0.5,
                        }}
                    >
                        {composition.roofShare > 0 && (
                            <Box
                                sx={{
                                    width: `${composition.roofShare * 100}%`,
                                    bgcolor: SHARE_COLORS.roof,
                                }}
                            />
                        )}
                        {composition.showGutter && composition.gutterShare > 0 && (
                            <Box
                                sx={{
                                    width: `${composition.gutterShare * 100}%`,
                                    bgcolor: SHARE_COLORS.gutter,
                                }}
                            />
                        )}
                        {composition.showFees && composition.feesShare > 0 && (
                            <Box
                                sx={{
                                    width: `${composition.feesShare * 100}%`,
                                    bgcolor: SHARE_COLORS.fees,
                                }}
                            />
                        )}
                    </Box>
                </Box>

                <Box sx={{ px: { xs: 1, sm: 1.5 } }}>
                    <CompositionRow
                        label="Roof"
                        value={composition.roof}
                        share={composition.roofShare}
                        color={SHARE_COLORS.roof}
                    />
                    {composition.showGutter && (
                        <CompositionRow
                            label="Gutters"
                            value={composition.gutter}
                            share={composition.gutterShare}
                            color={SHARE_COLORS.gutter}
                            showPlus
                        />
                    )}
                    {composition.showFees && (
                        <CompositionRow
                            label="Fees"
                            value={composition.fees}
                            share={composition.feesShare}
                            color={SHARE_COLORS.fees}
                            showPlus
                        />
                    )}
                </Box>

                <Box
                    sx={{
                        px: { xs: 2, sm: 2.5 },
                        py: 1.5,
                        borderTop: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Typography level="body-xs" sx={{ color: "text.tertiary", fontWeight: 600 }}>
                        Payment schedule
                    </Typography>
                    <Typography level="body-sm" sx={{ color: "text.secondary", mt: 0.25 }}>
                        {composition.paymentSchedule}
                    </Typography>
                </Box>
            </Box>

            <Typography level="body-xs" sx={{ color: "text.tertiary", mt: -0.5 }}>
                Same pricing engine as contract preview. Only cart lines with a supplier
                selected are included.
            </Typography>

            {/* Detail panels — only non-empty scopes */}
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                    gap: 2,
                }}
            >
                <SectionPanel
                    icon={<Layers size={16} />}
                    title="Roof"
                    subtitle="Materials + tax + labor, then profit margin"
                >
                    {sections.roofRows.map((row) => (
                        <BreakdownLine key={row.label} {...row} />
                    ))}
                </SectionPanel>

                {sections.hasGutterScope && (
                    <SectionPanel
                        icon={<Waves size={16} />}
                        title="Gutters & downspouts"
                        subtitle="Materials + labor, then profit margin"
                    >
                        {sections.gutterRows.map((row) => (
                            <BreakdownLine key={row.label} {...row} />
                        ))}
                    </SectionPanel>
                )}

                {sections.hasFees && (
                    <SectionPanel
                        icon={<PiggyBank size={16} />}
                        title="Fees"
                        subtitle="Insurance and overhead on direct roof + gutter cost"
                    >
                        {sections.markupRows.map((row) => (
                            <BreakdownLine key={row.label} {...row} />
                        ))}
                    </SectionPanel>
                )}

                {sections.hasUpgrades && (
                    <SectionPanel
                        icon={<Sparkles size={16} />}
                        title="Upgrades"
                        subtitle="Optional upgrades noted on the contract"
                    >
                        {sections.upgradeRows.map((row) => (
                            <BreakdownLine key={row.label} {...row} />
                        ))}
                    </SectionPanel>
                )}
            </Box>
        </Box>
    );
}
